import React, { useState } from "react";
import { Button, Input, Modal, Radio } from "antd";
import { useTranslation } from "react-i18next";

import { useDeleteEmailDocumentMutation } from "../../../api/graphql/mutations/deleteEmailDocument";
import { DeletionReason } from "../../../api/graphql/schema.generated";
import {
  destroyMessage,
  showErrorMessage,
  showLoadingMessage,
  showSuccessMessage,
} from "../../../utils";

const LOADING_KEY = "delete-email-document";

const DELETION_REASON_KEYS = [
  "DUPLICATE",
  "WRONG_RECEIPT",
  "NOT_A_RECEIPT",
  "UPLOADED_BY_MISTAKE",
  "OTHER",
] as const;

interface DeleteEmailDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  emailDocumentId: string;
}

const DeleteEmailDocumentModal: React.FC<DeleteEmailDocumentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  emailDocumentId,
}) => {
  const { t } = useTranslation("DeleteEmailDocumentModal");
  const [selectedReason, setSelectedReason] = useState<
    DeletionReason | undefined
  >(undefined);
  const [reasonText, setReasonText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [deleteEmailDocumentMutation] = useDeleteEmailDocumentMutation();

  const isConfirmEnabled =
    !!selectedReason &&
    (selectedReason !== "OTHER" || reasonText.trim().length > 0);

  const resetState = () => {
    setSelectedReason(undefined);
    setReasonText("");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedReason) return;

    setIsLoading(true);
    showLoadingMessage(LOADING_KEY);

    try {
      const { data } = await deleteEmailDocumentMutation({
        variables: {
          id: emailDocumentId,
          deletionReason: selectedReason,
          deletionReasonText:
            selectedReason === "OTHER" ? reasonText.trim() : undefined,
        },
      });
      if (!data?.deleteEmailDocument.success) {
        throw new Error("Deletion returned success: false");
      }
      destroyMessage(LOADING_KEY);
      showSuccessMessage(t("success"));
      resetState();
      onSuccess();
    } catch {
      destroyMessage(LOADING_KEY);
      showErrorMessage(t("error"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      title={t("title")}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={isLoading}>
          {t("cancel")}
        </Button>,
        <Button
          key="confirm"
          danger
          type="primary"
          disabled={!isConfirmEnabled}
          loading={isLoading}
          onClick={handleConfirm}
        >
          {t("confirm")}
        </Button>,
      ]}
    >
      <Radio.Group
        value={selectedReason}
        onChange={(e) => setSelectedReason(e.target.value)}
        style={{ display: "flex", flexDirection: "column", gap: 8 }}
      >
        {DELETION_REASON_KEYS.map((key) => (
          <Radio key={key} value={key}>
            {t(`reasons.${key}`)}
          </Radio>
        ))}
      </Radio.Group>
      {selectedReason === "OTHER" && (
        <Input.TextArea
          value={reasonText}
          onChange={(e) => setReasonText(e.target.value)}
          placeholder={t("placeholder") as string}
          rows={3}
          style={{ marginTop: 12 }}
        />
      )}
    </Modal>
  );
};

export default DeleteEmailDocumentModal;
